import { useCallback, useRef } from 'react';
import { useAtom } from 'jotai';
import { conversationsByFamilyAtom } from '../store/chat';
import type { ChatConversation } from '../components/chat/ConversationDialogs';

// The home's chat list, kept per family in the store (M66, D248): coming
// back to the home shows the family's list at once, with no loader, and a
// family switch never shows the previous family's chats. The atom itself is
// in store/chat.ts, where sign-out and leaving a family clear it.
export { conversationsByFamilyAtom };

const EMPTY: ChatConversation[] = [];

type Update =
  | ChatConversation[]
  | ((previous: ChatConversation[]) => ChatConversation[]);

export function useConversationCache(familyId: string | null) {
  const [byFamily, setByFamily] = useAtom(conversationsByFamilyAtom);
  const familyRef = useRef(familyId);
  familyRef.current = familyId;

  /** Replace or update one family's list; `familyId` defaults to the current one. */
  const setFor = useCallback(
    (id: string | null, update: Update) => {
      if (!id) return;
      setByFamily((map) => {
        const previous = map[id] ?? EMPTY;
        const next = typeof update === 'function' ? update(previous) : update;
        return next === previous ? map : { ...map, [id]: next };
      });
    },
    [setByFamily]
  );

  /** Update the current family's list (the setState the page is used to). */
  const setConversations = useCallback(
    (update: Update) => setFor(familyRef.current, update),
    [setFor]
  );

  return {
    conversations: familyId ? (byFamily[familyId] ?? EMPTY) : EMPTY,
    /** The family has a list from before: show it, no loader (D248). */
    cached: familyId !== null && byFamily[familyId] !== undefined,
    setConversations,
    setFor,
  };
}
